import React from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';

const AcceptableUsePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-sauna-beige py-12 px-4">
      <Helmet>
        <title>Acceptable Use Policy | SaunaDip</title>
        <meta name="description" content="Acceptable Use Policy for SaunaDip - Guidelines for using our sauna directory platform" />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-sauna-navy flex items-center justify-center gap-2">
            <ShieldCheck className="h-8 w-8 text-sauna-orange" />
            Acceptable Use Policy
          </h1>
          <div className="h-1 w-32 divider-gradient rounded-full mx-auto mt-4"></div>
        </div>
        <Card className="border-none shadow-lg">
          <CardContent className="p-8">
            <div className="prose prose-sauna max-w-none">
              <p className="text-gray-600 mb-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">1. Introduction</h2>
                <p className="text-sauna-navy/90 mb-4">
                  This Acceptable Use Policy ("Policy") outlines the terms and conditions for using SaunaDip's platform and services. 
                  By accessing or using our services, you agree to comply with this Policy. We reserve the right to modify this Policy 
                  at any time, and continued use of our services constitutes acceptance of any changes.
                </p>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">2. General Rules</h2>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>You must provide accurate and truthful information when creating listings or user accounts</li>
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You must not use our services for any illegal purposes or in violation of any local, state, national, or international laws</li>
                  <li>You must not attempt to interfere with, compromise, or disrupt our services or servers</li>
                  <li>You must not use automated systems or software to extract data from our platform ("scraping")</li>
                </ul>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">3. Content Guidelines</h2>
                <p className="text-sauna-navy/90 mb-4">When creating listings or posting content, you agree not to:</p>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>Post false, misleading, or deceptive information</li>
                  <li>Include content that is defamatory, obscene, or offensive</li>
                  <li>Upload images that are inappropriate, misleading, or violate copyright laws</li>
                  <li>Post content that promotes discrimination, harassment, or hate speech</li>
                  <li>Include personal information of others without their consent</li>
                  <li>Post spam, advertisements, or promotional content without authorization</li>
                </ul>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">4. Business Listings</h2>
                <p className="text-sauna-navy/90 mb-4">Business owners and operators must:</p>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>Maintain accurate and up-to-date business information</li>
                  <li>Respond to customer inquiries in a timely manner</li>
                  <li>Honor any advertised services, prices, and availability</li>
                  <li>Comply with all relevant health and safety regulations</li>
                  <li>Maintain appropriate insurance coverage</li>
                  <li>Not engage in fraudulent or deceptive business practices</li>
                </ul>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">5. User Reviews and Ratings</h2>
                <p className="text-sauna-navy/90 mb-4">When posting reviews or ratings, you must:</p>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>Provide honest and accurate feedback based on personal experience</li>
                  <li>Not post reviews on behalf of others or accept compensation for reviews</li>
                  <li>Not post reviews that are defamatory, abusive, or contain personal attacks</li>
                  <li>Not manipulate ratings through fake reviews or coordinated efforts</li>
                </ul>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">6. Privacy and Data Protection</h2>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>You must respect the privacy of other users and not collect their personal information without consent</li>
                  <li>You must comply with all applicable data protection laws and regulations</li>
                  <li>You must not use our services to collect or store personal data for unauthorized purposes</li>
                </ul>
              </section>
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">7. Enforcement</h2>
                <p className="text-sauna-navy/90 mb-4">
                  We reserve the right to take any of the following actions in response to violations of this Policy:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                  <li>Remove or modify content that violates this Policy</li>
                  <li>Suspend or terminate user accounts</li>
                  <li>Block access to our services</li>
                  <li>Report violations to law enforcement authorities</li>
                  <li>Take legal action against violators</li>
                </ul>
              </section>
              <section className="mb-4">
                <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">8. Contact Information</h2>
                <p className="text-sauna-navy/90">
                  If you have any questions about this Policy or need to report a violation, please contact us at:
                  <br />
                  <a href="mailto:support@saunadip.com" className="text-sauna-orange hover:text-sauna-orange/80">
                    support@saunadip.com
                  </a>
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptableUsePolicy; 